import { useContext, useEffect, useMemo, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import { IoMdRefresh } from "react-icons/io";
import LogicContext from "../../Context/LogicContext";
import GeneralCustomTable from "../../helpers/GeneralCustomTable";
import StatusBadge from "../../generals/StatusBadge";
import { designationService } from "../../services/designation.service";
import DesignationFormModal from "./DesignationFormModal";

function compareDates(left, right) {
  const leftTime = Date.parse(left || "");
  const rightTime = Date.parse(right || "");
  if (Number.isNaN(leftTime)) return Number.isNaN(rightTime) ? 0 : -1;
  if (Number.isNaN(rightTime)) return 1;
  return leftTime - rightTime;
}

function hasActiveStatus(designation) {
  return Number(designation.statusId) === 1;
}

function designationToastMessage(message) {
  return /designation/i.test(message) ? message : `Designation: ${message}`;
}

function DesignationListPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const logicContext = useContext(LogicContext);
  const [designations, setDesignations] = useState([]);
  const [selectedDesignations, setSelectedDesignations] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [departmentFilter, setDepartmentFilter] = useState("All");
  const [loading, setLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState("");

  const url = pathname.split("/");
  const currModuleId = url[1];
  const currModuleName = url[2];
  const currUserMenu = url[3] || "designations";
  const basePath = `/${currModuleId}/${currModuleName}/${currUserMenu}`;

  async function loadDesignations() {
    setLoading(true);
    setError("");
    try {
      const result = await designationService.getDesignations();
      setDesignations(result);
    } catch (loadError) {
      setError(
        loadError?.message
          ? designationToastMessage(loadError.message)
          : "Unable to load designations."
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateDesignation({ name, departmentId }) {
    setIsCreating(true);
    try {
      const result = await designationService.createDesignation(
        name,
        departmentId
      );
      logicContext.showToast(
        designationToastMessage(result.message),
        "success"
      );
      await loadDesignations();
    } catch (createError) {
      logicContext.showToast(
        createError?.message || "Unable to create designation.",
        "error"
      );
      throw createError;
    } finally {
      setIsCreating(false);
    }
  }

  useEffect(() => {
    document.title = "Designation Setup";
    loadDesignations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const departmentOptions = useMemo(() => {
    const byId = new Map();
    designations.forEach((designation) => {
      const id = designation.departmentId;
      if (id === null || id === undefined || id === "-") return;
      const key = String(id);
      if (!byId.has(key)) {
        byId.set(key, {
          id: key,
          name:
            designation.departmentName && designation.departmentName !== "-"
              ? designation.departmentName
              : `Department ${key}`,
        });
      }
    });
    return Array.from(byId.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [designations]);

  const filteredDesignations = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    return designations.filter((designation) => {
      const matchesSearch = String(designation.name ?? "")
        .toLowerCase()
        .includes(search);
      const matchesStatus =
        statusFilter === "All" ||
        (statusFilter === "Active" && hasActiveStatus(designation)) ||
        (statusFilter === "Inactive" && !hasActiveStatus(designation));
      const matchesDepartment =
        departmentFilter === "All" ||
        String(designation.departmentId) === departmentFilter;

      return matchesSearch && matchesStatus && matchesDepartment;
    });
  }, [designations, searchText, statusFilter, departmentFilter]);

  const columns = [
    {
      title: "Id",
      key: "id",
      dataIndex: "id",
      sorter: (a, b) => Number(a?.id ?? 0) - Number(b?.id ?? 0),
    },
    {
      title: "Name",
      key: "name",
      dataIndex: "name",
      className: "text-primary",
      sorter: (a, b) => (a?.name ?? "").localeCompare(b?.name ?? ""),
      render: (record, row) => (
        <Link
          className="text-primary"
          to={`${basePath}/${row.id}`}
          onClick={(event) => event.stopPropagation()}
        >
          {record}
        </Link>
      ),
    },
    {
      title: "Department",
      key: "departmentName",
      dataIndex: "departmentName",
      sorter: (a, b) =>
        (a?.departmentName ?? "").localeCompare(b?.departmentName ?? ""),
    },
    {
      title: "Status",
      key: "statusName",
      dataIndex: "statusName",
      sorter: (a, b) => (a?.statusName ?? "").localeCompare(b?.statusName ?? ""),
      render: (record, row) => (
        <StatusBadge
          statusId={hasActiveStatus(row) ? 1 : "I"}
          statusName={record}
        />
      ),
    },
    {
      title: "Created By",
      key: "createdBy",
      dataIndex: "createdBy",
      sorter: (a, b) => (a?.createdBy ?? "").localeCompare(b?.createdBy ?? ""),
    },
    {
      title: "Created At",
      key: "createdAt",
      dataIndex: "createdAt",
      sorter: (a, b) =>
        compareDates(a?.createdAtRaw, b?.createdAtRaw),
    },
  ];

  function handleRowClick(designation) {
    navigate(`${basePath}/${designation.id}`);
  }

  return (
    <div className="@container">
      <div className="@xl:flex @xl:justify-between">
        <div className="mt-4 flex flex-wrap gap-2">
          <div className="flex rounded bg-filterColor">
            <Dropdown>
              <Dropdown.Toggle
                style={{
                  backgroundColor: "#E5E5E5",
                  border: "none",
                  color: "#45444B",
                  fontSize: "12px",
                  height: "35px",
                }}
                id="designation-filter-dropdown"
              >
                FILTER BY DESIGNATION
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setStatusFilter("All")}>
                  All
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setStatusFilter("Active")}>
                  Active
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setStatusFilter("Inactive")}>
                  Inactive
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>

          <div className="flex rounded bg-filterColor">
            <Dropdown>
              <Dropdown.Toggle
                style={{
                  backgroundColor: "#E5E5E5",
                  border: "none",
                  color: "#45444B",
                  fontSize: "12px",
                  height: "35px",
                }}
                id="designation-department-filter-dropdown"
              >
                FILTER BY DEPARTMENT
              </Dropdown.Toggle>

              <Dropdown.Menu style={{ maxHeight: "300px", overflowY: "auto" }}>
                <Dropdown.Item
                  active={departmentFilter === "All"}
                  onClick={() => setDepartmentFilter("All")}
                >
                  All
                </Dropdown.Item>
                {departmentOptions.map((department) => (
                  <Dropdown.Item
                    key={department.id}
                    active={departmentFilter === department.id}
                    onClick={() => setDepartmentFilter(department.id)}
                  >
                    {department.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>

          <Tooltip title="Reload Designations">
            <button
              type="button"
              onClick={loadDesignations}
              disabled={loading || isCreating}
              className="group flex h-[35px] items-center justify-center px-2 border-[1px] border-gray-200 bg-white rounded-md hover:bg-gray-200 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
            >
              <IoMdRefresh className="h-6 w-6 text-gray-600 group-active:rotate-45 transition-all duration-300" />
            </button>
          </Tooltip>

          <input
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            className="h-[35px] w-full @sm:w-[260px] rounded-md border border-gray-200 px-3 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Search designations by name"
          />
        </div>

        <div className="flex items-center">
          {selectedDesignations.length > 0 ? (
            <div className="mt-4 mr-2 text-sm text-gray-600">
              {selectedDesignations.length} designations selected
            </div>
          ) : null}

          <button
            onClick={() => setShowCreateModal(true)}
            disabled={loading || isCreating}
            className="mt-4 py-2.5 px-6 text-sm rounded-md bg-blue-500 text-white font-medium focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
          >
            Add Designation
          </button>
        </div>
      </div>

      <div className="mt-5">
        {loading ? (
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="h-8 rounded bg-gray-100" />
            <div className="mt-3 h-12 rounded bg-gray-100" />
            <div className="mt-3 h-12 rounded bg-gray-100" />
            <div className="mt-3 h-12 rounded bg-gray-100" />
          </div>
        ) : null}

        {!loading && error ? (
          <div className="rounded-lg border border-red-100 bg-red-50 p-5 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        {!loading && !error && filteredDesignations.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-10 text-center text-sm text-gray-500">
            No designations found.
          </div>
        ) : null}

        {!loading && !error && filteredDesignations.length > 0 ? (
          <GeneralCustomTable
            tableHeadList={columns}
            tableDataList={filteredDesignations}
            allowCheckBox={false}
            rowKey={(record) => record.id}
            handleRowClick={handleRowClick}
            pageSizeOptions={["10", "25", "50"]}
          />
        ) : null}
      </div>

      {showCreateModal ? (
        <DesignationFormModal
          mode="create"
          initialValues={{ name: "", statusId: 2 }}
          onSubmit={handleCreateDesignation}
          onClose={() => setShowCreateModal(false)}
        />
      ) : null}
    </div>
  );
}

export default DesignationListPage;